/**
 * Período do Início. Os presets fecham no fim do dia de hoje (23:59:59), não
 * em "agora": assim o intervalo é o mesmo o dia inteiro e a chave do cache não
 * muda a cada render.
 */

export const PERIOD_PRESETS = {
  hoje: 'Hoje',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  mes: 'Este mês',
  mes_passado: 'Mês passado',
  custom: 'Personalizado',
} as const;
export type PeriodPreset = keyof typeof PERIOD_PRESETS;

/** Padrão do Início (Q2). */
export const DEFAULT_PRESET: PeriodPreset = '30d';

const startOfDay = (date: Date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};
const endOfDay = (date: Date) => {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
};
const fromDay = (day: string) => new Date(`${day}T00:00:00`);

export function resolvePeriod(
  preset: PeriodPreset,
  customFrom?: string | null,
  customTo?: string | null,
): { from: string; to: string } {
  const today = new Date();
  let from: Date;
  let to = endOfDay(today);
  switch (preset) {
    case 'hoje':
      from = startOfDay(today);
      break;
    case '7d':
      from = startOfDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6));
      break;
    case 'mes':
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case 'mes_passado':
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = endOfDay(new Date(today.getFullYear(), today.getMonth(), 0));
      break;
    case 'custom':
      from = customFrom ? fromDay(customFrom) : startOfDay(today);
      to = customTo ? endOfDay(fromDay(customTo)) : endOfDay(today);
      break;
    default:
      from = startOfDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29));
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

export const isPreset = (value: string | null): value is PeriodPreset =>
  value !== null && value in PERIOD_PRESETS;

/** "3d 4h", "2h 10min" — tempo médio na etapa e até concluir. */
export function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'menos de 1min';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return `${days}d${hours ? ` ${hours}h` : ''}`;
  const rest = minutes % 60;
  return hours > 0 ? `${hours}h${rest ? ` ${rest}min` : ''}` : `${rest}min`;
}

export const formatPercent = (value: number | null): string =>
  value === null ? '—' : `${(value * 100).toFixed(value < 0.1 && value > 0 ? 1 : 0)}%`;

/** "2026-10" → "out/26". */
export function monthLabel(month: string): string {
  const [year, number] = month.split('-').map(Number);
  const name = new Date(year, number - 1, 1).toLocaleDateString('pt-BR', { month: 'short' });
  return `${name.replace('.', '')}/${String(year).slice(2)}`;
}

/** "2026-10-19" → "19/10". */
export const dayLabel = (day: string): string => `${day.slice(8, 10)}/${day.slice(5, 7)}`;
